import { t } from "@hakili/i18n";
import { Button, LIEN_TEXTE } from "@hakili/ui";
import { Check, FileText, LayoutList, Columns3, Search } from "lucide-react";
import { useCallback, useEffect, useState, type ReactElement } from "react";

import type { DealerDossiersReadAnswer } from "../session/dealer-dossiers.js";
import type { DoorResult } from "../session/useSession.js";
import type { IssuedProforma } from "../session/vehicle-papers.js";
import { dayOf, sinceWords } from "./dossier-dates.js";
import { HakiliFace } from "./hakili-face.js";
import { IssuedNotice, issuedLineOf, RefusalNotice } from "./proforma-outcome.js";
import { ReadFailure } from "./read-failure.js";
import { ReceiptDialog } from "./receipt-dialog.js";

type Dossier = DealerDossiersReadAnswer["dossiers"][number];
type WaitingFilter = "all" | "dealer" | "client" | "bank" | "hakili";

/**
 * `P1-DLW-048` (DEALER V2, G-E1; the architect's rulings of 1 November 2026): **the dealership's dossier LIST**, to D2's frame 19 (`design/dealer-v2` `0880ca2b`, sources `1ec57eb9`).
 *
 * The browser prints what `GET /api/v1/me/dealership/dossiers` serves and writes none of it: the status, who waits, the next text, the row's action and its LABEL, the parcours and the counts of the tiles all come from the
 * door (`dealer-dossier-words.ts`). What is computed here is presentation only: the filter, the search, the age of « depuis » from the served `since`, and the view.
 *
 * **The row's button exists only where the door served an action** (`status.action`, a code and a target); a row served `null` shows the served `nextText` as text. « Émettre la facture » is one tap, no body. « Confirmer la réception »
 * opens a small dialog that asks the day (the dealer's own statement, A50): the date field defaults to today and is never in the future; the `Idempotency-Key` is minted ONCE per opening, so a double tap, or a retry after a refusal,
 * confirms once. A refusal of the server is drawn verbatim.
 *
 * NOT drawn, by ruling: « Nouveau dossier assisté » (B2's door), the phone number and any link on the row (B5-b adds the link and the page together). A failed read is the shared `ReadFailure`, and draws no tile, no list and no « 0 ».
 */
export interface DossiersProps {
  readonly readDossiers: () => Promise<DoorResult<DealerDossiersReadAnswer>>;
  readonly issueProforma: (input: {
    vehicleId: string;
    dossierId: string;
  }) => Promise<DoorResult<IssuedProforma>>;
  readonly confirmReceipt: (
    settlementId: string,
    receivedOn: string,
    idempotencyKey?: string,
  ) => Promise<DoorResult<unknown>>;
  /** `P1-DLW-058` (B5-b): the router's seam; with it a row's client and car are the link to the dossier's own page (`/dossiers/{id}`). */
  readonly naviguer?: ((path: string) => void) | undefined;
}

const WAITING_PILL: Readonly<Record<string, string>> = {
  dealer: "bg-amber-50 text-amber-700",
  bank: "bg-indigo-50 text-indigo-700",
  client: "bg-gray-50 text-gray-700",
  hakili: "bg-gray-50 text-gray-700",
};

/** The board's five columns, by the served step (the sixth, « Voiture remise », rides the last: the prototype's `step >= 4`). */
const COLUMNS: readonly { readonly key: string; readonly from: number; readonly to: number }[] = [
  { key: "dealer.dossiers.colonneDepot", from: 0, to: 0 },
  { key: "dealer.dossiers.colonneEtude", from: 1, to: 1 },
  { key: "dealer.dossiers.colonneOffre", from: 2, to: 2 },
  { key: "dealer.dossiers.colonneSignature", from: 3, to: 3 },
  { key: "dealer.dossiers.colonneVersement", from: 4, to: 99 },
];

/**
 * `P1-DLW-058` (B5-b; the row's link is the client AND the car, the architect's acceptance of 3 November): the row's identity block is the link to `/dossiers/{id}` where the router's seam is given, plain text otherwise (no dead link). The name keeps its test id and stays its own text.
 */
function RowIdentity(props: {
  readonly dossier: Dossier;
  readonly car: string;
  readonly naviguer: ((path: string) => void) | undefined;
}): ReactElement {
  const { dossier, car, naviguer } = props;
  const body = (
    <>
      <b
        className={`block text-sm ${naviguer === undefined ? "text-gray-900" : LIEN_TEXTE}`}
        data-testid="dealer-dossiers-nom"
      >
        {dossier.client.name}
      </b>
      <small className="block text-xs text-gray-500">{car}</small>
    </>
  );
  return naviguer === undefined ? (
    <div className="min-w-0">{body}</div>
  ) : (
    <button
      type="button"
      className="min-w-0 text-left"
      onClick={() => {
        naviguer(`/dossiers/${dossier.id}`);
      }}
    >
      {body}
    </button>
  );
}

/** The monogram of a person, shared with the Prospects screen (`P1-DLW-054`). */
export function Initials(props: { readonly name: string }): ReactElement {
  const words = props.name.trim().split(/\s+/u).filter(Boolean);
  const letters = (words[0]?.at(0) ?? "") + (words.length > 1 ? (words.at(-1)?.at(0) ?? "") : "");
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-900 text-sm font-semibold text-white"
    >
      {letters.toLocaleUpperCase("fr-FR")}
    </span>
  );
}

function ParcoursBar(props: { readonly dossier: Dossier }): ReactElement {
  const { steps, currentStep } = props.dossier.parcours;
  const label =
    currentStep === null
      ? undefined
      : t("dealer.dossiers.etape")
          .split("{n}")
          .join(String(currentStep + 1))
          .split("{total}")
          .join(String(steps.length))
          .split("{nom}")
          .join(steps[currentStep] ?? "");
  const waitsForDealer = props.dossier.status.waitingOn === "dealer";
  return (
    <div
      data-testid="dealer-dossiers-parcours"
      {...(label === undefined ? {} : { role: "img", "aria-label": label })}
      className="flex gap-1"
    >
      {steps.map((step, index) => (
        <i
          key={step}
          className={`block h-1.5 w-10 rounded-full ${
            currentStep === null
              ? "bg-gray-200"
              : index < currentStep
                ? "bg-emerald-600"
                : index === currentStep
                  ? waitsForDealer
                    ? "bg-amber-500"
                    : "bg-indigo-600"
                  : "bg-gray-200"
          }`}
        />
      ))}
    </div>
  );
}

export function Dossiers(props: DossiersProps): ReactElement {
  const { readDossiers, issueProforma, confirmReceipt } = props;
  const [today, setToday] = useState<string | null>(null);
  const [read, setRead] = useState<DoorResult<DealerDossiersReadAnswer> | null>(null);
  const [filter, setFilter] = useState<WaitingFilter>("all");
  const [origin, setOrigin] = useState<"client" | "concession" | null>(null);
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"liste" | "pipeline">("liste");
  const [rowError, setRowError] = useState<{ readonly id: string; readonly message: string } | null>(null);
  const [issuing, setIssuing] = useState<string | null>(null);
  /** A47: the one-tap proforma is never silent: what the door answered, said in words (the row loses its button when the list is read again). */
  const [issued, setIssued] = useState<{ readonly who: string; readonly line: string } | null>(null);
  const [receipt, setReceipt] = useState<{
    readonly dossier: Dossier;
    readonly settlementId: string;
    readonly key: string;
  } | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const answer = await readDossiers();
    // `OD-17`: the instant is the SERVER's, carried by the answer itself (`asOf`, P1-DOS-078). An answer without it dates nothing here.
    setToday(answer.ok && answer.value.asOf !== undefined ? dayOf(answer.value.asOf) : null);
    setRead(answer);
  }, [readDossiers]);

  useEffect(() => {
    void load();
  }, [load]);

  if (read === null) return <div data-testid="dealer-dossiers-chargement" aria-busy="true" />;

  if (!read.ok) {
    return (
      <section>
        <h1 className="text-2xl font-semibold text-gray-900" data-testid="dealer-dossiers-titre">
          {t("dealer.dossiers.titre")}
        </h1>
        <div className="mt-4">
          <ReadFailure
            what="dealer.lecture.dossiers"
            message={read.message}
            onRetry={() => {
              setRead(null);
              void load();
            }}
            testId="dealer-dossiers-echec"
            retryTestId="dealer-dossiers-echec-reessayer"
          />
        </div>
      </section>
    );
  }

  const answer = read.value;
  const counts = answer.counts;

  if (answer.dossiers.length === 0) {
    return (
      <section>
        <h1 className="text-2xl font-semibold text-gray-900" data-testid="dealer-dossiers-titre">
          {t("dealer.dossiers.titre")}
        </h1>
        <div
          className="mt-6 rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center"
          data-testid="dealer-dossiers-vide"
        >
          <HakiliFace />
          <h2 className="text-lg font-semibold text-gray-900">{t("dealer.dossiers.vide")}</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">{t("dealer.dossiers.videCorps")}</p>
        </div>
      </section>
    );
  }

  const needle = query.trim().toLocaleLowerCase("fr-FR");
  const shown = answer.dossiers.filter(
    (dossier) =>
      (filter === "all" || dossier.status.waitingOn === filter) &&
      (origin === null || dossier.origin.code === origin) &&
      (needle === "" ||
        `${dossier.client.name} ${dossier.vehicle.label}`.toLocaleLowerCase("fr-FR").includes(needle)),
  );

  const open = counts.all - counts.byWaitingOn.nobody;
  const subtitle =
    counts.byWaitingOn.nobody > 0
      ? t("dealer.dossiers.sousTitreClos")
          .split("{enCours}")
          .join(String(open))
          .split("{clos}")
          .join(String(counts.byWaitingOn.nobody))
      : t("dealer.dossiers.sousTitre").split("{enCours}").join(String(open));

  const tiles: readonly {
    readonly key: WaitingFilter;
    readonly count: number;
    readonly label: string;
    readonly sub: string;
  }[] = [
    {
      key: "all",
      count: counts.all,
      label: t("dealer.dossiers.tousLibelle"),
      sub: t("dealer.dossiers.tousSous"),
    },
    {
      key: "dealer",
      count: counts.byWaitingOn.dealer,
      label: t("dealer.dossiers.chezVousLibelle"),
      sub: t("dealer.dossiers.chezVousSous"),
    },
    {
      key: "client",
      count: counts.byWaitingOn.client,
      label: t("dealer.dossiers.chezClientLibelle"),
      sub: t("dealer.dossiers.chezClientSous"),
    },
    {
      key: "bank",
      count: counts.byWaitingOn.bank,
      label: t("dealer.dossiers.chezBanqueLibelle"),
      sub: t("dealer.dossiers.chezBanqueSous"),
    },
    {
      key: "hakili",
      count: counts.byWaitingOn.hakili,
      label: t("dealer.dossiers.chezHakiliLibelle"),
      sub: t("dealer.dossiers.chezHakiliSous"),
    },
  ];

  const act = async (dossier: Dossier): Promise<void> => {
    const action = dossier.status.action;
    if (action === null) return;
    setRowError(null);
    setIssued(null);
    if (action.code === "confirmerReception") {
      setReceipt({ dossier, settlementId: action.target.settlementId, key: globalThis.crypto.randomUUID() });
      return;
    }
    setIssuing(dossier.id);
    const done = await issueProforma({
      vehicleId: action.target.vehicleId,
      dossierId: action.target.dossierId,
    });
    setIssuing(null);
    if (!done.ok) {
      setRowError({ id: dossier.id, message: done.message });
      return;
    }
    setIssued({ who: `${dossier.client.name} · ${dossier.vehicle.label}`, line: issuedLineOf(done.value) });
    await load();
  };

  const actionOf = (dossier: Dossier): ReactElement | null => {
    const action = dossier.status.action;
    if (action === null) return null;
    return (
      <Button
        variant={dossier.status.waitingOn === "dealer" ? "primary" : "secondary"}
        {...(dossier.status.waitingOn === "dealer" ? { "data-awaits": "dealer" } : {})}
        size="sm"
        disabled={issuing === dossier.id}
        onClick={() => {
          void act(dossier);
        }}
        data-testid="dealer-dossiers-action"
      >
        {action.code === "emettreProforma" ? (
          <FileText className="mr-1.5 inline h-4 w-4" aria-hidden="true" />
        ) : (
          <Check className="mr-1.5 inline h-4 w-4" aria-hidden="true" />
        )}
        {action.label}
      </Button>
    );
  };

  const errorOf = (dossier: Dossier): ReactElement | null =>
    rowError?.id === dossier.id ? (
      <RefusalNotice message={rowError.message} testId="dealer-dossiers-erreur" />
    ) : null;

  const pill = (dossier: Dossier): ReactElement | null =>
    dossier.status.waitingText === null ? null : (
      <span
        className={`inline-flex max-w-full rounded-full px-3 py-1 text-xs font-semibold ${
          WAITING_PILL[dossier.status.waitingOn ?? ""] ?? "bg-gray-50 text-gray-700"
        }`}
        data-testid="dealer-dossiers-attente"
      >
        {dossier.status.waitingText}
      </span>
    );

  const carOf = (dossier: Dossier): string =>
    dossier.vehicle.stockReference === null
      ? dossier.vehicle.label
      : `${dossier.vehicle.label} · ${dossier.vehicle.stockReference}`;

  return (
    <section>
      <header>
        <h1 className="m-0 text-2xl font-semibold text-gray-900" data-testid="dealer-dossiers-titre">
          {t("dealer.dossiers.titre")}
        </h1>
        <p className="m-0 mt-1 text-sm text-gray-500" data-testid="dealer-dossiers-sous-titre">
          {subtitle}
        </p>
      </header>

      {issued === null ? null : (
        <div className="mt-4">
          <IssuedNotice who={issued.who} line={issued.line} testId="dealer-dossiers-proforma-emise" />
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="dealer-dossiers-tuiles">
        {tiles.map((tile) => (
          <button
            key={tile.key}
            type="button"
            aria-pressed={filter === tile.key}
            data-testid="dealer-dossiers-tuile"
            data-filtre={tile.key}
            onClick={() => {
              setFilter(tile.key);
            }}
            className={`rounded-2xl border px-4 py-3 text-left ${
              filter === tile.key ? "border-indigo-600 ring-1 ring-indigo-600" : "border-gray-200"
            } ${tile.key === "dealer" ? "border-l-4 border-l-indigo-600 bg-indigo-50" : "bg-white"}`}
          >
            <span className="block text-3xl font-semibold tabular-nums text-gray-900">{tile.count}</span>
            <span className="block text-sm font-semibold text-gray-900">{tile.label}</span>
            <span className="block text-xs text-gray-500">{tile.sub}</span>
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <label className="relative min-w-[16rem] flex-1">
          <span className="sr-only">{t("dealer.dossiers.rechercheEtiquette")}</span>
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            placeholder={t("dealer.dossiers.recherche")}
            autoComplete="off"
            data-testid="dealer-dossiers-recherche"
            className="block w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900"
          />
        </label>
        <div
          role="group"
          aria-label={t("dealer.dossiers.canal")}
          className="inline-flex overflow-hidden rounded-xl border border-gray-200 bg-white text-sm font-medium"
        >
          {(
            [
              ["client", t("dealer.dossiers.canalClients"), counts.byOrigin.client],
              ["concession", t("dealer.dossiers.canalConcession"), counts.byOrigin.concession],
            ] as const
          ).map(([code, label, count]) => (
            <button
              key={code}
              type="button"
              aria-pressed={origin === code}
              data-testid="dealer-dossiers-canal"
              onClick={() => {
                setOrigin(origin === code ? null : code);
              }}
              className={`px-4 py-2 first:border-r first:border-gray-200 ${
                origin === code ? "bg-gray-900 text-white" : "text-gray-700"
              }`}
            >
              {`${label} · ${String(count)}`}
            </button>
          ))}
        </div>
        <div
          role="group"
          aria-label={t("dealer.dossiers.affichage")}
          className="inline-flex overflow-hidden rounded-xl border border-gray-200 bg-white text-sm font-medium"
        >
          <button
            type="button"
            aria-pressed={view === "liste"}
            data-testid="dealer-dossiers-vue-liste"
            onClick={() => {
              setView("liste");
            }}
            className={`inline-flex items-center gap-1.5 px-4 py-2 ${view === "liste" ? "bg-indigo-600 text-white" : "text-gray-700"}`}
          >
            <LayoutList className="h-4 w-4" aria-hidden="true" />
            {t("dealer.dossiers.vueListe")}
          </button>
          <button
            type="button"
            aria-pressed={view === "pipeline"}
            data-testid="dealer-dossiers-vue-pipeline"
            onClick={() => {
              setView("pipeline");
            }}
            className={`inline-flex items-center gap-1.5 px-4 py-2 ${view === "pipeline" ? "bg-indigo-600 text-white" : "text-gray-700"}`}
          >
            <Columns3 className="h-4 w-4" aria-hidden="true" />
            {t("dealer.dossiers.vuePipeline")}
          </button>
        </div>
      </div>

      {view === "liste" ? (
        shown.length === 0 ? (
          <div
            className="mt-4 rounded-2xl border border-gray-200 bg-white px-6 py-9 text-center"
            data-testid="dealer-dossiers-aucun-resultat"
          >
            <h2 className="m-0 text-lg font-semibold text-gray-900">{t("dealer.dossiers.aucunResultat")}</h2>
            <p className="m-0 mt-1 text-sm text-gray-500">{t("dealer.dossiers.aucunResultatCorps")}</p>
          </div>
        ) : (
          <ul className="m-0 mt-4 grid list-none gap-3 p-0" aria-label={t("dealer.dossiers.titre")}>
            {shown.map((dossier) => (
              <li
                key={dossier.id}
                data-testid="dealer-dossiers-ligne"
                className={`flex flex-wrap items-center gap-4 rounded-2xl border border-gray-200 px-5 py-4 ${
                  dossier.status.waitingOn === null ? "bg-gray-50" : "bg-white"
                }`}
              >
                <div className="flex min-w-[14rem] items-center gap-3">
                  <Initials name={dossier.client.name} />
                  <RowIdentity dossier={dossier} car={carOf(dossier)} naviguer={props.naviguer} />
                </div>
                <div className="min-w-[16rem] flex-1">
                  <ParcoursBar dossier={dossier} />
                  <b className="mt-2 block text-sm text-gray-900">{dossier.status.whereText}</b>
                  <small className="block text-xs text-gray-500">{sinceWords(dossier.since, today)}</small>
                </div>
                <div className="min-w-[10rem]">{pill(dossier)}</div>
                <div className="ml-auto flex min-w-[12rem] max-w-xs flex-col items-end gap-2 text-right">
                  {dossier.status.action === null ? (
                    dossier.status.nextText === null ? null : (
                      <span className="text-xs text-gray-500" data-testid="dealer-dossiers-ensuite">
                        {dossier.status.nextText}
                      </span>
                    )
                  ) : (
                    actionOf(dossier)
                  )}
                  {errorOf(dossier)}
                  <span className="rounded-full bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700">
                    {dossier.origin.words}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )
      ) : (
        <div className="mt-4 grid gap-3 md:grid-cols-5" data-testid="dealer-dossiers-pipeline">
          {COLUMNS.map((column) => {
            const label = t(column.key);
            const cards = shown.filter(
              (dossier) =>
                dossier.parcours.currentStep !== null &&
                dossier.parcours.currentStep >= column.from &&
                dossier.parcours.currentStep <= column.to,
            );
            return (
              <section
                key={column.key}
                aria-label={label}
                data-testid="dealer-dossiers-colonne"
                className="rounded-2xl border border-gray-200 bg-gray-50 p-3"
              >
                <h2 className="m-0 mb-2 text-sm font-semibold text-gray-900">
                  {label} <span className="rounded-full bg-white px-2 py-0.5 text-xs">{cards.length}</span>
                </h2>
                {cards.length === 0 ? (
                  <p className="m-0 text-sm text-gray-500">{t("dealer.dossiers.colonneVide")}</p>
                ) : (
                  cards.map((dossier) => (
                    <article key={dossier.id} className="mb-2 rounded-xl border border-gray-200 bg-white p-3">
                      <div className="flex items-center gap-2">
                        <Initials name={dossier.client.name} />
                        <RowIdentity
                          dossier={dossier}
                          car={dossier.vehicle.label}
                          naviguer={props.naviguer}
                        />
                      </div>
                      <div className="mt-2">{pill(dossier)}</div>
                      <small className="mt-1 block text-xs text-gray-500">
                        {sinceWords(dossier.since, today)}
                      </small>
                      <div className="mt-2">
                        {dossier.status.action === null ? (
                          dossier.status.nextText === null ? null : (
                            <small className="text-xs text-gray-500">{dossier.status.nextText}</small>
                          )
                        ) : (
                          actionOf(dossier)
                        )}
                        {errorOf(dossier)}
                      </div>
                    </article>
                  ))
                )}
              </section>
            );
          })}
        </div>
      )}

      {receipt === null ? null : (
        <ReceiptDialog
          key={receipt.key}
          today={today}
          onCancel={() => {
            setReceipt(null);
          }}
          onConfirm={(receivedOn) => confirmReceipt(receipt.settlementId, receivedOn, receipt.key)}
          onDone={() => {
            setReceipt(null);
            void load();
          }}
        />
      )}
    </section>
  );
}
